<template>
  <span class="toolbar-wrapper">
    <ToolBar class="toolbar">
      <template #start>
        <div class="logo-container">
          <img class="logo" alt="Estudyoo" src="@/assets/logo.svg" />
        </div>
      </template>
      <template #center v-if="formTitle">
        <div class="toolbar-title-block">
          <div class="toolbar-title-row">
            <h2 class="form-title">{{ formTitle }}</h2>
            <ButtonPrime
              v-if="screenHelp"
              type="button"
              class="toolbar-help-btn"
              icon="pi pi-question-circle"
              text
              rounded
              aria-label="Ayuda de esta pantalla"
              v-tooltip="{ value: screenHelp.summary, position: 'bottom' }"
              @click="helpOpen = true"
            />
          </div>
          <DialogPrime
            v-if="screenHelp"
            v-model:visible="helpOpen"
            modal
            header="Ayuda de la pantalla"
            :style="{ width: 'min(28rem, 92vw)' }"
            :dismissableMask="true"
            :breakpoints="{ '576px': '95vw' }"
          >
            <p class="toolbar-help-lead">{{ screenHelp.summary }}</p>
            <p v-for="(p, i) in screenHelp.paragraphs" :key="i" class="toolbar-help-p">{{ p }}</p>
          </DialogPrime>
        </div>
      </template>
      <template #end>
        <div class="toolbar-end-cluster">
          <DebugNotesCollector />
          <ButtonPrime
            type="button"
            class="toolbar-menu-btn"
            icon="pi pi-bars"
            rounded
            aria-label="Abrir menú lateral"
            v-tooltip="{ value: 'Menú', position: 'bottom' }"
            @click.prevent="openSideBar"
          />
        </div>
      </template>
    </ToolBar>

    <SidebarPrime
      v-model:visible="visibleRight"
      position="right"
      class="toolbar-sidebar"
      :style="{ width: 'min(22rem, 92vw)' }"
    >
      <div class="sidebar-menu-head">
        <span>Navegación</span>
      </div>
      <MenuPrime :model="navMenu" class="toolbar-sidebar-menu" />
    </SidebarPrime>
  </span>
</template>

<script>
import { useRoute } from 'vue-router';
import { computed, ref, watch } from 'vue';
import DebugNotesCollector from '@/components/DebugNotesCollector.vue';
import { getScreenHelp } from '@/content/screenHelpEs';

export default {
  name: 'ToolbarComp',
  components: { DebugNotesCollector },
  setup() {
    const route = useRoute();
    const helpOpen = ref(false);

    const formTitle = computed(() => {
      return route.meta.formulario || '';
    });

    const screenHelp = computed(() => getScreenHelp(route));

    watch(
      () => route.name,
      () => {
        helpOpen.value = false;
      }
    );

    return {
      formTitle,
      screenHelp,
      helpOpen,
    };
  },
  data() {
    return {
      visibleRight: false,
    };
  },
  computed: {
    /** Menú alineado con el panel de inicio y las rutas reales del router. */
    navMenu() {
      const go = (name, params) => {
        this.$router.push(params ? { name, params } : { name });
        this.visibleRight = false;
      };
      return [
        {
          label: 'Inicio',
          items: [
            { label: 'Panel inicio', icon: 'pi pi-home', command: () => go('home') },
            { label: 'Monitoreo del servidor', icon: 'pi pi-chart-line', command: () => go('monitoreo-servidor') },
          ],
        },
        {
          label: 'Usuarios y comunicación',
          items: [
            { label: 'Administrar usuarios', icon: 'pi pi-users', command: () => go('admin-alumno') },
            { label: 'Asignatura — usuarios', icon: 'pi pi-id-card', command: () => go('admin-asignatura') },
            { label: 'Uso alumnos', icon: 'pi pi-chart-line', command: () => go('uso-alumnos') },
            { label: 'Notificar alumnos', icon: 'pi pi-bell', command: () => go('notificar-alumnos') },
            { label: 'Nuevo usuario', icon: 'pi pi-user-plus', command: () => go('admin-alumnos-nuevo') },
            { label: 'Uso e informes (panel)', icon: 'pi pi-th-large', command: () => go('admin-uso') },
            { label: 'Actualizar casas (masivo)', icon: 'pi pi-map-marker', command: () => go('actualiza-masivo-casas-alumno') },
          ],
        },
        {
          label: 'Uso e informes',
          items: [
            { label: 'Uso por curso (informe)', icon: 'pi pi-chart-bar', command: () => go('uso-curso') },
            { label: 'Uso video por curso', icon: 'pi pi-video', command: () => go('uso-video') },
            { label: 'Uso test por curso', icon: 'pi pi-pencil', command: () => go('uso-test') },
            { label: 'Uso práctica', icon: 'pi pi-check-square', command: () => go('uso-practica') },
            { label: 'Uso práctica múltiple', icon: 'pi pi-list', command: () => go('uso-practica-multiple') },
            { label: 'Cumplimiento prácticas', icon: 'pi pi-percentage', command: () => go('cumplimiento-practicas') },
            { label: 'Informe interacción test', icon: 'pi pi-chart-line', command: () => go('informe-interaccion-test') },
            { label: 'Informe cumplimiento test', icon: 'pi pi-check-circle', command: () => go('informe-cumplimiento-test') },
            { label: 'Informe visualización video', icon: 'pi pi-eye', command: () => go('informe-visualizacion-video') },
            { label: 'Informe interacción juego', icon: 'pi pi-trophy', command: () => go('informe-interaccion-juego') },
            { label: 'Informe cumplimiento juego', icon: 'pi pi-table', command: () => go('informe-cumplimiento-juego') },
            { label: 'Informe interacción total', icon: 'pi pi-sitemap', command: () => go('informe-interaccion-total') },
          ],
        },
        {
          label: 'Menú masivo',
          items: [
            { label: 'Panel menú masivo', icon: 'pi pi-bars', command: () => go('admin-masivo') },
            { label: 'Ingreso masivo alumno', icon: 'pi pi-upload', command: () => go('ingreso-masivo-alumno') },
            { label: 'Ingreso masivo apoderado', icon: 'pi pi-users', command: () => go('ingreso-masivo-apoderado') },
            { label: 'Ingreso masivo profesor', icon: 'pi pi-briefcase', command: () => go('ingreso-masivo-profesor') },
            { label: 'Ingreso videos (masivo)', icon: 'pi pi-video', command: () => go('ingreso-masivo-videos') },
            { label: 'Ingreso capítulos', icon: 'pi pi-book', command: () => go('ingreso-masivo-capitulos') },
            { label: 'Relación video – capítulo', icon: 'pi pi-link', command: () => go('relacion-video-capitulo') },
            { label: 'Jefaturas', icon: 'pi pi-building', command: () => go('ingreso-jefaturas') },
          ],
        },
        {
          label: 'Preguntas y calidad',
          items: [
            { label: 'Reporte imágenes preguntas', icon: 'pi pi-images', command: () => go('reporte-imagenes-preguntas') },
            { label: 'Ingreso preguntas (nuevo)', icon: 'pi pi-pencil', command: () => go('ingreso-preguntas-nuevo') },
            { label: 'Ingreso preguntas SIMCE', icon: 'pi pi-file', command: () => go('ingreso-preguntas-simce') },
            { label: 'Habilidades (catálogo)', icon: 'pi pi-bolt', command: () => go('mantenedor-habilidades') },
            { label: 'Preguntas (admin clásico)', icon: 'pi pi-question-circle', command: () => go('admin-preguntas') },
            { label: 'Ingreso masivo preguntas', icon: 'pi pi-table', command: () => go('ingreso-masivo-preguntas') },
          ],
        },
        {
          label: 'Capítulos y catálogos',
          items: [
            { label: 'Capítulos no vigentes', icon: 'pi pi-book', command: () => go('capitulos-no-vigente') },
            { label: 'Orden capítulos en unidades', icon: 'pi pi-sort-alt', command: () => go('orden-capitulos-en-unidades') },
            { label: 'Mantenedor capítulos', icon: 'pi pi-folder', command: () => go('mantenedor-capitulos') },
            { label: 'Mantenedor unidades', icon: 'pi pi-sitemap', command: () => go('mantenedor-unidades') },
            { label: 'Mantenedor asignaturas', icon: 'pi pi-bookmark', command: () => go('mantenedor-asignaturas') },
            { label: 'Mantenedor videos', icon: 'pi pi-video', command: () => go('mantenedor-videos') },
            { label: 'Mantenedor contratos', icon: 'pi pi-file', command: () => go('mantenedor-contratos') },
          ],
        },
        {
          label: 'Actividades Estudyoo',
          items: [
            { label: 'Hub Actividades Estudyoo', icon: 'pi pi-sparkles', command: () => go('estudyoo-hub') },
            { label: 'Inventario y conteos', icon: 'pi pi-chart-bar', command: () => go('estudyoo-inventario') },
            { label: 'Mapa capítulos y actividades', icon: 'pi pi-sitemap', command: () => go('estudyoo-mapa-capitulos') },
            { label: 'Asignar actividades por curso', icon: 'pi pi-share-alt', command: () => go('estudyoo-asignaciones') },
            { label: 'Sincronizar desde legacy', icon: 'pi pi-cloud-upload', command: () => go('sync-django') },
            { label: 'Generación con IA', icon: 'pi pi-bolt', command: () => go('estudyoo-actividades-ia') },
            { label: 'Mantenedor actividades', icon: 'pi pi-palette', command: () => go('estudyoo-actividades') },
            { label: 'Mantenedor juegos', icon: 'pi pi-star', command: () => go('estudyoo-juegos') },
            { label: 'Panel SIMCE', icon: 'pi pi-th-large', command: () => go('simce-admin-dashboard') },
          ],
        },
        {
          label: 'Cuenta',
          items: [
            { label: 'Cerrar sesión', icon: 'pi pi-power-off', command: () => go('logout') },
          ],
        },
      ];
    },
  },
  methods: {
    openSideBar() {
      this.visibleRight = true;
    },
  },
};
</script>

<style scoped>
.toolbar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
}

.toolbar {
  height: 3.5rem;
  width: 100%;
  padding: 0 0.75rem 0 0.5rem;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid var(--admin-border, rgba(15, 23, 42, 0.08));
  border-radius: 0;
  background: rgba(255, 255, 255, 0.92) !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8) inset, 0 4px 20px rgba(15, 23, 42, 0.06);
}

.toolbar :deep(.p-toolbar-group-start),
.toolbar :deep(.p-toolbar-group-center),
.toolbar :deep(.p-toolbar-group-end) {
  align-items: center;
}

.logo-container {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  overflow: hidden;
}

.logo {
  width: 2.25rem;
  height: auto;
  display: block;
}

.toolbar-title-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: min(56vw, 36rem);
}

.toolbar-title-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.toolbar-help-btn {
  flex-shrink: 0;
  color: var(--admin-text-muted, #64748b) !important;
}

.toolbar-help-btn:hover {
  color: var(--primary-color) !important;
}

.toolbar-help-lead {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--admin-text, #1e293b);
  line-height: 1.45;
}

.toolbar-help-p {
  margin: 0 0 0.65rem;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--admin-text-muted, #475569);
}

.toolbar-help-p:last-child {
  margin-bottom: 0;
}

.form-title {
  font-family: var(--admin-font, inherit);
  font-size: clamp(0.95rem, 2.5vw, 1.15rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--admin-text, #1e293b);
  margin: 0;
  line-height: 1.2;
}

/* Botón menú (hamburguesa): más claro y táctil */
.toolbar-end-cluster {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.toolbar-menu-btn {
  flex-shrink: 0;
}

.toolbar-menu-btn :deep(.p-button) {
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: 1px solid var(--admin-border, rgba(15, 23, 42, 0.1));
  background: #fff !important;
  color: var(--admin-text, #334155) !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
}

.toolbar-menu-btn :deep(.p-button:hover) {
  background: #f8fafc !important;
  border-color: rgba(37, 99, 235, 0.35) !important;
  color: var(--primary-color) !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
}

.toolbar-menu-btn :deep(.p-button:focus) {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(37, 99, 235, 0.35);
}

.toolbar-menu-btn :deep(.p-button .pi) {
  font-size: 1.25rem;
}

.toolbar-sidebar :deep(.p-sidebar-content) {
  padding: 0;
}

.sidebar-menu-head {
  padding: 0.9rem 1.1rem 0.65rem;
  border-bottom: 1px solid var(--admin-border, rgba(15, 23, 42, 0.1));
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--admin-text-muted, #64748b);
}

.toolbar-sidebar-menu {
  width: 100%;
  max-width: 22rem;
  border: none;
}

.toolbar-sidebar-menu :deep(.p-menu) {
  width: 100%;
  border: none;
  max-height: min(70vh, 32rem);
  overflow-y: auto;
}

.toolbar-sidebar-menu :deep(.p-submenu-header) {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--admin-text-muted, #64748b);
  padding: 0.6rem 1rem 0.25rem;
}

.toolbar-sidebar-menu :deep(.p-menuitem-link) {
  border-radius: 8px;
  margin: 0.1rem 0.4rem;
  padding: 0.5rem 0.65rem;
}
</style>
